import { useId, type FormEvent } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { AlertBanner } from '@/components/molecules/AlertBanner';
import { describeActiveDiscount } from '@/features/billing/checkout-code-helpers';
import { PromoCodeDisclosure } from '@/features/billing/PromoCodeDisclosure';
import type { PlanUpgradeFlow } from './use-plan-upgrade';

type Props = {
  /** The promo slice of the upgrade flow — draft / applied / error / gate. */
  promo: PlanUpgradeFlow['promo'];
  /** The preview is re-quoting under the newly applied code. */
  applying: boolean;
};

/**
 * Promo-code field inside the self-serve upgrade confirm dialog. Split out of
 * `PlanUpgradeDialog` purely to keep that file under the 250-line limit — the
 * state lives in `usePlanUpgrade`, this is presentation only.
 *
 * "Apply" doesn't call an endpoint of its own: it commits the draft into the
 * preview's query key, so the confirm dialog re-quotes WITH the discount and the
 * proration summary's existing Discount row (`buildProrationRows`) fills in from
 * Paddle's real figures. A rejected code therefore arrives as a failed preview,
 * which the hook classifies by `errorCode` and hands back as `promo.error`.
 *
 * The field is collapsed behind a `PromoCodeDisclosure` because it sits directly
 * under the proration figures the dialog is asking the customer to confirm — an
 * empty text box there reads as an unfinished step in the purchase rather than
 * the optional aside it is.
 *
 * NOT shared with `CheckoutDialog`'s promo input despite the near-identical
 * copy: that one validates against `POST /checkout/validate-code` and carries a
 * `paddleDiscountId` through to Paddle.js, this one validates by refetching a
 * quote. The shared pieces are `checkout-code-helpers.ts`, which owns every
 * `errorCode` → copy mapping both use, and the disclosure wrapper.
 */
export function UpgradePromoField({ promo, applying }: Props) {
  const inputId = useId();
  const blockedCopy = describeActiveDiscount(promo.activeDiscount);
  const trimmed = promo.draft.trim();
  // Only claim success once the re-quote has actually landed — mid-refetch the
  // code hasn't been accepted by anything yet.
  const accepted = promo.appliedCode != null && promo.error == null && !applying;

  function handleApply(e: FormEvent) {
    e.preventDefault();
    if (trimmed === '' || applying) return;
    promo.apply();
  }

  // Paddle holds exactly one discount per subscription, so a second code is a
  // guaranteed rejection. Nothing to reveal — state the block and stop.
  if (blockedCopy != null) {
    return (
      <div className="border-t border-[color:var(--ink-100)] pt-3">
        <AlertBanner variant="info">{blockedCopy}</AlertBanner>
      </div>
    );
  }

  return (
    <PromoCodeDisclosure
      className="flex flex-col gap-2 border-t border-[color:var(--ink-100)] pt-3"
      // A code applied or rejected before this mounted must not hide behind the
      // link — the customer would have no reason to click it to find out.
      defaultOpen={promo.appliedCode != null || promo.error != null}
    >
      {({ autoFocus }) => (
        <>
          <label htmlFor={inputId} className="text-xs font-medium text-muted-foreground">
            Promo code
          </label>
          <form className="flex items-start gap-2" onSubmit={handleApply}>
            <Input
              id={inputId}
              autoFocus={autoFocus}
              value={promo.draft}
              onChange={(e) => promo.setDraft(e.target.value)}
              placeholder="Promo code (optional)"
              className="h-9"
            />
            <Button
              type="submit"
              variant="outline"
              size="sm"
              className="h-9"
              disabled={trimmed === '' || applying}
            >
              {applying ? 'Checking…' : 'Apply'}
            </Button>
          </form>

          {promo.error ? (
            <AlertBanner variant="error" action={<RemoveCodeButton onRemove={promo.remove} />}>
              {promo.error}
            </AlertBanner>
          ) : accepted ? (
            <AlertBanner variant="success" action={<RemoveCodeButton onRemove={promo.remove} />}>
              Code {promo.appliedCode} applied.
            </AlertBanner>
          ) : null}
        </>
      )}
    </PromoCodeDisclosure>
  );
}

/** Drops back to the undiscounted quote, which is still cached under the
 * no-code preview key — so it reappears without a network round-trip. */
function RemoveCodeButton({ onRemove }: { onRemove: () => void }) {
  return (
    <Button variant="ghost" size="sm" onClick={onRemove}>
      Remove code
    </Button>
  );
}
